"use client"

import * as React from "react"
import { LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UIInput } from '../forms/ui-input'
import { UISeparator } from '../layout/ui-separator'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardSignupProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children' | 'onSubmit'> {
  title?: string
  description?: string
  emailPlaceholder?: string
  submitText?: string
  onSubmit?: (email: string) => void
  socialOptions?: Array<{
    icon: LucideIcon
    label: string
    onClick: () => void
  }>
  children?: React.ReactNode
}

/**
 * Sign Up Card Component
 * Complete sign-up form with email input and social options
 * Based on shadcn/ui "Sign Up Card" block
 */
export const UICardSignup = React.forwardRef<HTMLDivElement, UICardSignupProps>(
  ({
    title = "Create an account",
    description = "Start your free 30 days trial. Cancel at anytime",
    emailPlaceholder = "Enter your email",
    submitText = "Get Started",
    onSubmit,
    socialOptions = [],
    className,
    children,
    ...props
  }, ref) => {
    const [email, setEmail] = React.useState("")

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (email && onSubmit) {
        onSubmit(email)
      }
    }

    return (
      <UICard
        ref={ref}
        className={cn("w-full max-w-md", className)}
        {...props}
      >
        {/* Header */}
        <UICardHeader className="text-center">
<UICardTitle>
            <UITypography variant="h3" size="xl" weight="semibold">
              {title}
            </UITypography>
          </UICardTitle>
          {description && (
<UICardDescription>
              <UITypography variant="p" size="sm" color="muted">
                {description}
              </UITypography>
            </UICardDescription>
          )}
        </UICardHeader>

        {/* Content */}
        <UICardContent className="space-y-4">
          {/* Email form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <UIBox className="space-y-2">
              <UIInput
                type="email"
                placeholder={emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </UIBox>

            <UIButton
              type="submit"
              variant="default"
              className="w-full"
              disabled={!email}
            >
              {submitText}
            </UIButton>
          </form>

          {/* Social options */}
          {socialOptions.length > 0 && (
            <>
              <UIBox className="relative">
                <UISeparator />
                <UIBox className="absolute inset-0 flex items-center justify-center">
<UITypography as="span" variant="caption" size="xs" color="muted" className="bg-background px-2">
                    Or continue with
                  </UITypography>
                </UIBox>
              </UIBox>

              <UIBox className="grid gap-2">
                {socialOptions.map((option, index) => {
                  const Icon = option.icon
                  return (
                    <UIButton
                      key={index}
                      variant="outline"
                      onClick={option.onClick}
                      className="w-full"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {option.label}
                    </UIButton>
                  )
                })}
              </UIBox>
            </>
          )}

          {/* Custom content */}
          {children}
        </UICardContent>

        {/* Footer */}
        <UICardFooter className="justify-center">
<UITypography variant="p" size="xs" color="muted" className="text-center">
            By creating an account, you agree to our{" "}
            <UITypography as="button" variant="p" size="xs" className="underline hover:text-foreground">
              Terms of Service
            </UITypography>{" "}
            and{" "}
            <UITypography as="button" variant="p" size="xs" className="underline hover:text-foreground">
              Privacy Policy
            </UITypography>
          </UITypography>
        </UICardFooter>
      </UICard>
    )
  }
)

UICardSignup.displayName = "UICardSignup"